import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import {
    DynamicFormButtonShapes,
    DynamicFormButtonShapesPill,
    DynamicFormButtonShapesRectangle,
    DynamicFormButtonShapesRounded,
    DynamicFormButtonTypes,
    DynamicFormButtonTypesOutline,
    DynamicFormButtonTypesSolid,
    DynamicFormButtonVariants,
    DynamicFormButtonVariantsPrimary,
    DynamicFormButtonVariantsSecondary,
    DynamicFormButtonVariantsTertiary,
    DynamicFormCheckboxLabelPositionsRight,
    DynamicFormEmailRegExp,
    DynamicFormFieldModel,
    DynamicFormFileUploadLabelPositionsTop,
    DynamicFormTextFieldLabelPositionsTop,
    DynamicFormThirdPartyApiTypes,
    DynamicFormThirdPartyApiTypesCustom,
} from "@midland-industries/Types/DynamicFormModels";
import DynamicForm from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import DynamicFormCheckbox from "@midland-industries/Widgets/DynamicForms/DynamicFormCheckbox";
import DynamicFormFileUpload from "@midland-industries/Widgets/DynamicForms/DynamicFormFileUpload";
import DynamicFormSelect from "@midland-industries/Widgets/DynamicForms/DynamicFormSelect";
import DynamicFormTextField from "@midland-industries/Widgets/DynamicForms/DynamicFormTextField";
import React, { useEffect } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
    buttonVariant = "buttonVariant",
    buttonShape = "buttonShape",
    buttonType = "buttonType",
    enableRecaptcha = "enableRecaptcha",
    sendToThirdPartyApi = "sendToThirdPartyApi",
    sendToApiType = "sendToApiType",
    customApiEndpoint = "customApiEndpoint",
    displayCustomApiResponse = "displayCustomApiResponse",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
        [fields.buttonVariant]: DynamicFormButtonVariants;
        [fields.buttonShape]: DynamicFormButtonShapes;
        [fields.buttonType]: DynamicFormButtonTypes;
        [fields.enableRecaptcha]: boolean;
        [fields.sendToThirdPartyApi]: boolean;
        [fields.sendToApiType]: DynamicFormThirdPartyApiTypes;
        [fields.customApiEndpoint]: string;
        [fields.displayCustomApiResponse]: boolean;
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    const session = getSession(state);
    const billToId = session.billToId;
    return {
        dynamicFormData,
        billToId,
        billToState: getBillToState(state, billToId),
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
    loadBillTo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface BinLabelOnDemandFormStyles {
    wrapper?: InjectableCss;
    formWrapper?: InjectableCss;
    fieldWrapper?: InjectableCss;
}

export const binLabelOnDemandFormStyles: BinLabelOnDemandFormStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    formWrapper: {
        css: css`
            width: 100%;
            margin-top: 20px;
            margin-bottom: 20px;
        `,
    },
    fieldWrapper: {
        css: css`
            width: 100%;
            margin-bottom: 20px;
            min-width: fit-content;
            display: flex;
            flex-direction: row;
        `,
    },
};

const styles = binLabelOnDemandFormStyles;

const BinLabelOnDemandForm: FC<Props> = props => {
    const { id, fields, dynamicFormData, billToState, billToId, setDynamicFormFieldValue, loadBillTo } = props;
    const {
        emailRecipients,
        submitButtonText,
        successMessage,
        buttonVariant,
        buttonShape,
        buttonType,
        enableRecaptcha,
        sendToThirdPartyApi,
        sendToApiType,
        customApiEndpoint,
        displayCustomApiResponse,
    } = fields;

    const formName = "Bin Labels on Demand";

    const getDynamicFormIndex = () => {
        const formIndex = dynamicFormData.forms.findIndex(f => f.name === formName);
        return formIndex;
    };

    const getWidgetFromState = (formIndex: number, sourceKey: string) => {
        if (formIndex < 0) {
            return undefined;
        }
        const sourceIndex = dynamicFormData.forms[formIndex].fields.findIndex(f => f.key === sourceKey);
        if (sourceIndex < 0) {
            return undefined;
        }
        const sourceField = dynamicFormData.forms[formIndex].fields[sourceIndex];
        return sourceField;
    };

    if (billToId && !billToState.value && !billToState.isLoading) {
        loadBillTo({ billToId });
    }

    const formIndex = dynamicFormData.forms.findIndex(f => f.name === formName);
    if (formIndex > -1) {
        if (billToState.value && !billToState.isLoading) {
            const companyNameField = getWidgetFromState(formIndex, "company_name");
            if (companyNameField && companyNameField.value.length === 0 && billToState.value) {
                setDynamicFormFieldValue({
                    formName,
                    formField: {
                        key: companyNameField.key,
                        group: companyNameField.group,
                        label: companyNameField.label,
                        isRequired: companyNameField.isRequired,
                        value: billToState.value.customerName,
                        type: companyNameField.type,
                        changeTrueFalseToYesNo: companyNameField.changeTrueFalseToYesNo,
                        sendOnlyIfChecked: companyNameField.sendOnlyIfChecked,
                        hasError: companyNameField.hasError,
                        error: companyNameField.error,
                    },
                });
            }
        }
    }

    useEffect(() => {
        if (billToState.value) {
            let companyNameField: DynamicFormFieldModel | undefined;
            const formIndex = getDynamicFormIndex();
            if (formIndex > -1) {
                companyNameField = getWidgetFromState(formIndex, "company_name");
            }
            if (companyNameField && companyNameField.value.length === 0 && billToState.value) {
                setDynamicFormFieldValue({
                    formName,
                    formField: {
                        key: companyNameField.key,
                        group: companyNameField.group,
                        label: companyNameField.label,
                        isRequired: companyNameField.isRequired,
                        value: billToState.value.customerName,
                        type: companyNameField.type,
                        changeTrueFalseToYesNo: companyNameField.changeTrueFalseToYesNo,
                        sendOnlyIfChecked: companyNameField.sendOnlyIfChecked,
                        hasError: companyNameField.hasError,
                        error: companyNameField.error,
                    },
                });
            }
        }
    }, [billToState]);

    // Get the widget for isLogoRequired
    let includeLogoField: DynamicFormFieldModel | undefined;

    if (formIndex > -1) {
        includeLogoField = getWidgetFromState(formIndex, "is_logo_required");
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <DynamicForm
                id={id}
                fields={{
                    formName,
                    emailRecipients,
                    submitButtonText,
                    successMessage,
                    buttonVariant,
                    buttonShape,
                    buttonType,
                    showResetButton: false,
                    enableRecaptcha,
                    sendToThirdPartyApi,
                    sendToApiType,
                    customApiEndpoint,
                    endpointRequiresAuth: false,
                    endpointUserId: "",
                    endpointPassword: "",
                    displayCustomApiResponse,
                }}
            >
                <StyledWrapper {...styles.formWrapper}>
                    <StyledWrapper {...styles.fieldWrapper}>
                        <DynamicFormCheckbox
                            fields={{
                                key: "is_logo_required",
                                group: "",
                                labelPosition: DynamicFormCheckboxLabelPositionsRight,
                                label: "Include logo on generated labels",
                                isRequired: false,
                                isCheckedByDefault: false,
                                changeTrueFalseToYesNo: true,
                                hintText: "Including your logo will replace your Company Name on printed labels.",
                            }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.fieldWrapper}>
                        <DynamicFormTextField
                            fields={{
                                key: "email",
                                labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                label: "Enter Email Address to receive generated PDF",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: false,
                                maxLength: 100,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: true,
                            }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.fieldWrapper}>
                        <DynamicFormTextField
                            isDisabled={includeLogoField?.value}
                            fields={{
                                key: "company_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                label: "Name to print on label",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 50,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.fieldWrapper}>
                        <DynamicFormSelect
                            fields={{
                                key: "label_type",
                                hasLabel: true,
                                label: "Label Type",
                                hintText: "",
                                isRequired: true,
                                options: [
                                    {
                                        fields: {
                                            label: 'Small (2.625" x 1")',
                                            value: "small",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: 'Large (4" x 2")',
                                            value: "large",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: 'Custom 1 (2.5" x 1.563")',
                                            value: "custom_1",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: 'Business Card (3.5" x 2")',
                                            value: "large_test",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: 'Custom 2 (3.4375" x .9375")',
                                            value: "custom_2",
                                        },
                                    },
                                ],
                            }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.fieldWrapper}>
                        <DynamicFormCheckbox
                            fields={{
                                key: "use_first_row",
                                group: "",
                                labelPosition: DynamicFormCheckboxLabelPositionsRight,
                                label: "First row of spreadsheet contains column headers",
                                isRequired: false,
                                isCheckedByDefault: false,
                                changeTrueFalseToYesNo: true,
                                hintText: "",
                            }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.fieldWrapper}>
                        <DynamicFormFileUpload
                            fields={{
                                key: "bin_label_file",
                                labelPosition: DynamicFormFileUploadLabelPositionsTop,
                                label: "Bin Label File",
                                isRequired: true,
                                allowedFileExtensions: [
                                    { fields: { fileExtension: "xls" } },
                                    { fields: { fileExtension: "xlsx" } },
                                    { fields: { fileExtension: "csv" } },
                                ],
                            }}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </DynamicForm>
        </StyledWrapper>
    );
};

const formTab = {
    displayName: "Form",
    sortOrder: 0,
};

const buttonTab = {
    displayName: "Button",
    sortOrder: 1,
};

const actionTab = {
    displayName: "Actions",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BinLabelOnDemandForm),
    definition: {
        group: "Basic",
        displayName: "Bin Labels on Demand Form",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: DynamicFormEmailRegExp,
                tab: formTab,
                sortOrder: 2,
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
                tab: formTab,
                sortOrder: 3,
            },
            {
                name: fields.enableRecaptcha,
                displayName: "Include ReCaptcha",
                tooltip: "Enabling this setting adds a ReCaptcha element to the form which must be clicked to submit.",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 4,
                tab: formTab,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Submit",
                tab: buttonTab,
                sortOrder: 11,
            },
            {
                name: fields.buttonVariant,
                displayName: "Select Button Variant",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormButtonVariantsPrimary,
                fieldType: "General",
                options: [
                    {
                        displayName: "Primary",
                        value: DynamicFormButtonVariantsPrimary,
                    },
                    {
                        displayName: "Secondary",
                        value: DynamicFormButtonVariantsSecondary,
                    },
                    {
                        displayName: "Tertiary",
                        value: DynamicFormButtonVariantsTertiary,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 12,
            },
            {
                name: fields.buttonShape,
                displayName: "Select Button Shape",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormButtonShapesRectangle,
                fieldType: "General",
                options: [
                    {
                        displayName: "Rectangle",
                        value: DynamicFormButtonShapesRectangle,
                    },
                    {
                        displayName: "Pill",
                        value: DynamicFormButtonShapesPill,
                    },
                    {
                        displayName: "Rounded",
                        value: DynamicFormButtonShapesRounded,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 13,
            },
            {
                name: fields.buttonType,
                displayName: "Select Button Type",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormButtonTypesSolid,
                fieldType: "General",
                options: [
                    {
                        displayName: "Solid",
                        value: DynamicFormButtonTypesSolid,
                    },
                    {
                        displayName: "Outline",
                        value: DynamicFormButtonTypesOutline,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 14,
            },
            {
                name: fields.sendToThirdPartyApi,
                displayName: "Upload FormData to 3rd Party System",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 200,
                tab: actionTab,
            },
            {
                name: fields.sendToApiType,
                displayName: "Select 3rd Party System Type",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormThirdPartyApiTypesCustom,
                fieldType: "General",
                options: [
                    {
                        displayName: "Custom API Endpoint",
                        value: DynamicFormThirdPartyApiTypesCustom,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                isVisible: widget => widget.fields.sendToThirdPartyApi,
                tab: actionTab,
                sortOrder: 210,
            },
            {
                name: fields.customApiEndpoint,
                displayName: "Custom API Endpoint URL",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget =>
                    widget.fields.sendToThirdPartyApi &&
                    widget.fields.sendToApiType === DynamicFormThirdPartyApiTypesCustom,
                tab: actionTab,
                sortOrder: 220,
            },
            {
                name: fields.displayCustomApiResponse,
                displayName: "Display results from API below Form for testing",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 230,
                tab: actionTab,
            },
        ],
    },
};

export default widgetModule;
